import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InnerHero } from '@/components/sections'
import { Prefooter, Footer } from '@/components/layout'

type Section = {
  id: string
  titleKey: string
  bodyKeys: string[]
  listKeys?: string[]
}

export function PrivacyPolicyPage() {
  const { t } = useTranslation('common')
  const [activeId, setActiveId] = useState('titolare')

  const sections: Section[] = [
    {
      id: 'titolare',
      titleKey: 'privacy.sections.titolare.title',
      bodyKeys: ['privacy.sections.titolare.p1', 'privacy.sections.titolare.p2'],
    },
    {
      id: 'finalita',
      titleKey: 'privacy.sections.finalita.title',
      bodyKeys: ['privacy.sections.finalita.p1'],
      listKeys: [
        'privacy.sections.finalita.l1',
        'privacy.sections.finalita.l2',
        'privacy.sections.finalita.l3',
        'privacy.sections.finalita.l4',
        'privacy.sections.finalita.l5',
        'privacy.sections.finalita.l6',
      ],
    },
    {
      id: 'destinatari',
      titleKey: 'privacy.sections.destinatari.title',
      bodyKeys: [
        'privacy.sections.destinatari.p1',
        'privacy.sections.destinatari.p2',
        'privacy.sections.destinatari.p3',
      ],
    },
    {
      id: 'cookie',
      titleKey: 'privacy.sections.cookie.title',
      bodyKeys: ['privacy.sections.cookie.p1'],
    },
    {
      id: 'social',
      titleKey: 'privacy.sections.social.title',
      bodyKeys: ['privacy.sections.social.p1'],
    },
    {
      id: 'extraUe',
      titleKey: 'privacy.sections.extraUe.title',
      bodyKeys: ['privacy.sections.extraUe.p1'],
    },
    {
      id: 'diritti',
      titleKey: 'privacy.sections.diritti.title',
      bodyKeys: ['privacy.sections.diritti.p1', 'privacy.sections.diritti.p3'],
      listKeys: [
        'privacy.sections.diritti.l1',
        'privacy.sections.diritti.l2',
        'privacy.sections.diritti.l3',
        'privacy.sections.diritti.l4',
        'privacy.sections.diritti.l5',
        'privacy.sections.diritti.l6',
        'privacy.sections.diritti.l7',
      ],
    },
  ]

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
            setActiveId(entry.target.id)
          }
        })
      },
      { rootMargin: '-30% 0px -50% 0px', threshold: [0, 0.3, 0.6, 1] },
    )
    sections.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [])

  return (
    <main className="bg-cream">
      <InnerHero
        eyebrow={t('privacy.hero.eyebrow')}
        title={t('privacy.hero.title')}
        titleItalic={t('privacy.hero.titleItalic')}
        body={t('privacy.hero.body')}
        breadcrumbs={[
          { label: t('breadcrumbs.home'), href: '/it' },
          { label: t('privacy.hero.breadcrumb') },
        ]}
      />

      {/* Welcome line */}
      <section className="relative bg-cream py-16 md:py-20 border-b border-bruno/10 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-12 right-[-4%] select-none font-display italic text-argan/[0.05]"
          style={{ fontSize: 'clamp(10rem, 22vw, 22rem)', lineHeight: 0.85 }}
        >
          §
        </div>
        <div className="relative nashi-container max-w-4xl">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7 }}
            className="nashi-eyebrow block"
          >
            {t('privacy.welcome.eyebrow')}
          </motion.span>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="mt-6 font-display italic text-bruno"
            style={{
              fontSize: 'clamp(1.5rem, 2.4vw, 2rem)',
              lineHeight: 1.4,
              letterSpacing: '-0.005em',
            }}
          >
            {t('privacy.welcome.intro')}
          </motion.p>
        </div>
      </section>

      {/* Body grid: sticky index + sections */}
      <section className="relative bg-cream py-20 md:py-28 overflow-hidden">
        <div className="nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Left rail — sticky index */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-32">
                <span className="nashi-eyebrow">{t('privacy.index.title')}</span>
                <ol className="mt-8 space-y-3">
                  {sections.map((s, i) => (
                    <li key={s.id} className="leading-tight">
                      <a
                        href={`#${s.id}`}
                        className={[
                          'group flex items-baseline gap-3 text-sm md:text-[15px] transition-colors',
                          activeId === s.id
                            ? 'text-argan-deep'
                            : 'text-bruno-soft hover:text-bruno',
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'tabular-nums text-[10px] tracking-[0.2em] uppercase shrink-0 mt-0.5 w-7',
                            activeId === s.id ? 'text-argan-deep' : 'text-bruno-muted',
                          ].join(' ')}
                        >
                          {String(i + 1).padStart(2, '0')}.
                        </span>
                        <span className="flex-1">{t(s.titleKey)}</span>
                      </a>
                    </li>
                  ))}
                </ol>

                <div className="mt-12 pt-8 border-t border-bruno/10">
                  <p className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                    {t('privacy.index.lastUpdateLabel')}
                  </p>
                  <p className="mt-3 font-display italic text-argan-deep text-2xl">
                    {t('privacy.index.lastUpdateDate')}
                  </p>
                </div>

                <div className="mt-10">
                  <a
                    href="mailto:privacy@landoll.it"
                    className="group inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.24em] text-bruno pb-2 border-b border-bruno/40 hover:border-argan-deep hover:text-argan-deep transition-colors"
                  >
                    privacy@landoll.it
                    <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5">
                      →
                    </span>
                  </a>
                </div>
              </div>
            </aside>

            {/* Right — sections */}
            <div className="lg:col-span-8 space-y-20 md:space-y-24">
              {sections.map((s, i) => (
                <motion.article
                  key={s.id}
                  id={s.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.9 }}
                  className="scroll-mt-32"
                >
                  <header className="flex items-baseline gap-5 mb-8 pb-5 border-b border-bruno/10">
                    <span className="font-display italic text-argan-deep text-2xl md:text-3xl tabular-nums">
                      {String(i + 1).padStart(2, '0')}.
                    </span>
                    <h2
                      className="font-display text-bruno"
                      style={{
                        fontSize: 'clamp(1.5rem, 2.8vw, 2.25rem)',
                        lineHeight: 1.1,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {t(s.titleKey)}
                    </h2>
                  </header>

                  <div className="space-y-5 text-bruno-soft text-[15px] md:text-base leading-[1.75] max-w-2xl">
                    {s.bodyKeys.map((key, idx) => (
                      <p key={idx}>{t(key)}</p>
                    ))}
                    {s.listKeys && (
                      <ul className="space-y-3 my-6 pl-0">
                        {s.listKeys.map((itemKey, j) => (
                          <li key={j} className="flex items-start gap-3 pl-0">
                            <span
                              aria-hidden="true"
                              className="mt-2.5 h-px w-4 shrink-0 bg-argan"
                            />
                            <span>{t(itemKey)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </motion.article>
              ))}

              {/* Closing notice */}
              <motion.aside
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9 }}
                className="bg-cream-soft border-l-2 border-argan p-8 md:p-10"
              >
                <p className="text-[10px] uppercase tracking-[0.32em] text-argan-deep mb-4">
                  {t('privacy.closing.label')}
                </p>
                <p className="text-bruno-soft text-[15px] leading-[1.75] max-w-2xl">
                  {t('privacy.closing.text')}
                </p>
              </motion.aside>
            </div>
          </div>
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
